import {
  createRxDatabase,
  addRxPlugin,
} from "rxdb"
import type { RxDatabase } from "rxdb"
import { RxDBDevModePlugin } from "rxdb/plugins/dev-mode"
import { RxDBQueryBuilderPlugin } from "rxdb/plugins/query-builder"
import { getRxStorageLoki } from "rxdb/plugins/storage-lokijs"
import { observeNewCollections } from "rxdb-hooks"
import {
  accountInsulinSchema,
  accountMedicationSchema,
  entryTemplateSchema,
  insulinSchema,
  medicationSchema,
  userEntrySchema,
  userEventSchema,
} from "src/schema"
import { CollectionKey } from "src/types"

// eslint-disable-next-line
const LokiIncrementalIndexedDBAdapter = require("lokijs/src/incremental-indexeddb-adapter")

// side effects
addRxPlugin(RxDBQueryBuilderPlugin)
addRxPlugin(observeNewCollections)

// in development run in dev mode
if (process.env?.NODE_ENV === "development") {
  addRxPlugin(RxDBDevModePlugin)
}

/*
 * the dbName will change to accommodate major schema shifts since we do not
 * support migrations in our frontend db. when the db name changes the entire
 * db is effectively dropped and will be reloaded from the server
 *
 * the next time the dbName changes:
 * - add the db version to the UserLocalStorageKey values used for loading states
 * - remove export_source and export_source_id from user entry subentries schema
 * - remove export_source and export_source_id from entry template subentries schema
 */
export const dbName = "sugarmate_web_app_db_4_3"

/*
 * creates a sugarmate database and its collections
 */
export async function createSugarmateRxDatabase(): Promise<RxDatabase> {
  const database = await createRxDatabase({
    name: dbName,
    storage: getRxStorageLoki({
      // eslint-disable-next-line
      adapter: new LokiIncrementalIndexedDBAdapter(),
    }),
    eventReduce: true,
    multiInstance: true,
    ignoreDuplicate: true,
  })

  await database.addCollections({
    [CollectionKey.AccountInsulin]: { schema: accountInsulinSchema },
    [CollectionKey.AccountMedication]: { schema: accountMedicationSchema },
    [CollectionKey.EntryTemplate]: { schema: entryTemplateSchema },
    [CollectionKey.Insulin]: { schema: insulinSchema },
    [CollectionKey.Medication]: { schema: medicationSchema },
    [CollectionKey.UserEntry]: { schema: userEntrySchema },
    [CollectionKey.UserEvent]: { schema: userEventSchema },
  })

  return database
}
